/**
 * Essentially forked from usehooks-ts/useElementSize
 *
 * following issue ticket https://github.com/juliencrn/usehooks-ts/issues/236
 * to utilize the resizeObserver to catch size changes during mount/unmount
 */

import { RefObject, useRef, useState } from 'react';
import { useIsomorphicLayoutEffect } from 'usehooks-ts';

interface Size {
  width: number;
  height: number;
}

export const useElementSizeWithResizeObserver = <T extends HTMLElement = HTMLDivElement>(): [
  RefObject<T>,
  Size,
] => {
  const ref = useRef<T>(null);
  const [size, setSize] = useState<Size>({
    width: 0,
    height: 0,
  });

  useIsomorphicLayoutEffect(() => {
    const updateSize = (element: Element | null) => {
      const { width, height } = element?.getBoundingClientRect() ?? {
        width: 0,
        height: 0,
      };
      setSize({ width, height });
    };

    updateSize(ref.current);

    const resizeObserver = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) {
        updateSize(entry.target);
      }
    });

    ref.current && resizeObserver.observe(ref.current);
    return () => {
      ref.current && resizeObserver.unobserve(ref.current);
    };
  }, [ref.current]);

  return [ref, size];
};
